import { useState, useEffect, useMemo } from 'react';
import { FiArrowLeft, FiPlus, FiZap, FiFileText, FiMail, FiVideo, FiFilter } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useHubSpotPortals } from '../hooks/useHubSpotPortals';
import { salesContentApi } from '../services/api';
import SalesContentModal from './SalesContentModal';

export default function SalesContentAgent() {
  const { selectedPortal } = useHubSpotPortals();
  const [allContentItems, setAllContentItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isCreating] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(null);
  const itemsPerPage = 25;

  // Fetch sales content from API
  useEffect(() => {
    if (!selectedPortal?.portal_id) return;

    const fetchContent = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await salesContentApi.fetchSalesContent(selectedPortal.portal_id);
        
        const mappedItems = (data.content || []).map(item => ({
          id: item.id,
          contentType: item.type || 'Document',
          name: item.title || 'Untitled',
          summary: item.summary || '',
          dateAdded: item.date_added || '',
          audience: item.audience || '',
          url: item.url || '',
        }));
        
        setAllContentItems(mappedItems);
      } catch (err) {
        console.error('Error fetching sales content:', err);
        setError('Failed to load sales content. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [selectedPortal?.portal_id]);

  // Filter + paginate
  const filteredItems = useMemo(() => {
    return allContentItems.filter(item => 
      !typeFilter || item.contentType.toLowerCase().includes(typeFilter.toLowerCase())
    );
  }, [allContentItems, typeFilter]);

  // eslint-disable-next-line no-unused-vars
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

  const contentTypes = useMemo(() => {
    const types = [...new Set(allContentItems.map(item => item.contentType))];
    return types.sort();
  }, [allContentItems]);

  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter]);

  const handleAddContent = () => {
    setShowModal(true);
    setError(null);
    setCreateSuccess(null);
  };

  const handleModalClose = () => {
    if (!isCreating) {
      setShowModal(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this content? This action cannot be undone.")) {
      return;
    }

    try {
      await salesContentApi.deleteSalesContent(id);
      setAllContentItems(prev => prev.filter(item => item.id !== id));
      setDeleteSuccess("Content deleted successfully.");
      setTimeout(() => setDeleteSuccess(null), 3000);
    } catch (err) {
      console.error("Error deleting content:", err);
      setError("Failed to delete content. Please try again.");
    }
  };

  const getContentTypeIcon = (contentType) => {
    switch (contentType.toLowerCase()) {
      case 'email template':
        return FiMail;
      case 'presentation':
        return FiVideo;
      case 'script':
        return FiFileText;
      default:
        return FiFileText;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Link
              to="/agents"
              className="flex items-center text-gray-500 hover:text-gray-700 mr-4"
            >
              <FiArrowLeft className="w-4 h-4 mr-1" />
              Back to Agents
            </Link>
            <div>
              <div className="flex items-center mb-1">
                <FiZap className="w-6 h-6 text-blue-600 mr-3" />
                <h1 className="text-2xl font-bold text-gray-900">Sales Content Agent</h1>
              </div>
              <p className="text-gray-600">
                Manage and create sales content with AI assistance
              </p>
            </div>
          </div>
          <button
            onClick={handleAddContent}
            className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-md transition-colors"
          >
            <FiPlus className="w-4 h-4 mr-2" />
            Add Content
          </button>
        </div>
      </div>

      {/* Messages */}
      {createSuccess && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-800">{createSuccess}</p>
        </div>
      )}
      {deleteSuccess && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-800">{deleteSuccess}</p>
        </div>
      )}

      {/* Content Table */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        {/* Filter & Table Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Content Library</h2>
            <p className="text-sm text-gray-600 mt-1">
              {selectedPortal ? `Showing content for ${selectedPortal.hub_domain}` : 'Select a portal to view content'}
            </p>
          </div>
          {contentTypes.length > 0 && (
            <div className="flex items-center space-x-2">
              <FiFilter className="w-4 h-4 text-gray-500" />
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                {contentTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Loading / Error / Table */}
        {loading && <div className="px-6 py-8 text-center text-gray-500">Loading sales content...</div>}
        {error && <div className="px-6 py-4 bg-red-50 border-b border-red-200 text-red-800 text-sm">{error}</div>}

        {selectedPortal && !loading && !error && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Content Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Summary</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedItems.length > 0 ? (
                  paginatedItems.map(item => {
                    const Icon = getContentTypeIcon(item.contentType);
                    return (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <Icon className="w-4 h-4 text-gray-500 mr-2" />
                            <span className="text-sm font-medium text-gray-900">{item.contentType}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {item.summary && item.summary.length > 100
                            ? `${item.summary.slice(0, 100)}...`
                            : item.summary}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {item.url && (
                            <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 mr-4">
                              View
                            </a>
                          )}
                          <button style={{display: 'none', marginRight: '10px'}} className="text-blue-600 hover:text-blue-800 mr-4">Edit</button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                      <FiFileText className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      {typeFilter ? `No content found for type "${typeFilter}"` : 'No sales content found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <SalesContentModal
          onClose={handleModalClose}
          portalId={selectedPortal?.portal_id}
        />
      )}
    </div>
  );
}
